import Modal from "@/components/controls/Modal";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { ConvertToContractData, DealData } from "@/pages/deals/types";
import Form, { FormField } from "@/components/controls/Form/Form";
import { currencyCodeFromSetting } from "@/utils/numbers";
import {
  useAccountSettingByKey,
  useCurrencies,
  useProducts
} from "@/entities/settings/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import { ConvertToContractBanner } from "@dreamhub/designsystem/icons";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { EntityType } from "@/services/hooks/entities/useEntityType";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deal: DealData | null;
  onSubmit: (contractData: ConvertToContractData) => void;
  "data-test"?: string;
}

const generateContractName = (companyName: string | undefined): string => {
  if (!companyName) return "";
  const now = new Date();
  return `${companyName} ${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const ConvertDealToContractModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  deal,
  onSubmit,
  "data-test": dataTest
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  // Form remount key — incremented to reset the form when product change is
  // cancelled or confirmed (so defaults reflect the correct product).
  const [formKey, setFormKey] = useState(0);

  // Tracks the last confirmed product ID so we can restore it on cancel.
  const [confirmedProductId, setConfirmedProductId] = useState<string>(
    deal?.productId ?? ""
  );

  // Product change warning dialog state.
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);
  const [showProductWarning, setShowProductWarning] = useState(false);
  const [productChangeAcknowledged, setProductChangeAcknowledged] =
    useState(false);

  // Sync confirmedProductId when a different deal opens.
  useEffect(() => {
    setConfirmedProductId(deal?.productId ?? "");
    setFormKey((k) => k + 1);
  }, [deal?.id]);

  const { data: productsData } = useProducts();
  const { data: currencies } = useCurrencies();
  const { data: accountCurrencySetting } =
    useAccountSettingByKey("account_currency");
  // Narrowed rather than cast — this seeds the form's default currency below,
  // and the settings union permits a boolean.
  const accountCurrency = currencyCodeFromSetting(accountCurrencySetting?.value);

  const {
    options: aeOwnerOptions,
    isLoading: isLoadingAeOwners,
    setSearchTerm: setAeOwnerSearchTerm,
    renderUserIcon: renderAeOwnerIcon
  } = useUserSelect();

  const {
    options: csmOwnerOptions,
    isLoading: isLoadingCsmOwners,
    setSearchTerm: setCsmOwnerSearchTerm,
    renderUserIcon: renderCsmOwnerIcon
  } = useUserSelect();

  const {
    options: companySelectOptions,
    isLoading: isLoadingCompanies,
    onSearch: onCompanySearch
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: deal?.companyId,
    alwaysEnabled: true
  });

  const productOptions = useMemo(
    () =>
      productsData?.products?.map((product) => ({
        id: product.id,
        displayName: product.name
      })) ?? [],
    [productsData?.products]
  );

  const currencyOptions = useMemo(
    () =>
      currencies?.map((currency) => ({
        id: currency,
        displayName: currency
      })) ?? [],
    [currencies]
  );

  const defaultContractName = useMemo(
    () => generateContractName(deal?.companyName),
    [deal?.companyName]
  );

  useEffect(() => {
    if (!isOpen) {
      setIsFormValid(false);
      setConfirmedProductId(deal?.productId ?? "");
      setPendingProductId(null);
      setShowProductWarning(false);
      setProductChangeAcknowledged(false);
      setFormKey((k) => k + 1);
    }
  }, [isOpen]);

  const defaultCurrency = deal?.currency || accountCurrency || "";

  const handleProductChange = (newProductId: string) => {
    if (newProductId === confirmedProductId) return;
    setPendingProductId(newProductId);
    setProductChangeAcknowledged(false);
    setShowProductWarning(true);
  };

  const handleProductWarningCancel = () => {
    setShowProductWarning(false);
    setPendingProductId(null);
    // Remount form so the product field reverts to the last confirmed product.
    setFormKey((k) => k + 1);
  };

  const handleProductWarningConfirm = () => {
    if (pendingProductId) {
      setConfirmedProductId(pendingProductId);
    }
    setShowProductWarning(false);
    setPendingProductId(null);
    // Remount form with the new confirmed product as the default.
    setFormKey((k) => k + 1);
  };

  const formFields: FormField[] = [
    {
      name: "contractName",
      label: t("deals.convertToContract.contractName"),
      type: "text",
      required: true,
      defaultValue: defaultContractName,
      placeholder: t("deals.convertToContract.contractNamePlaceholder"),
      labelPosition: "left"
    },
    {
      name: "companyId",
      label: t("deals.convertToContract.company"),
      type: "singleSelect",
      required: true,
      options: companySelectOptions,
      defaultValue: deal?.companyId || "",
      placeholder: t("deals.convertToContract.selectCompany"),
      showSearch: true,
      onSearch: onCompanySearch,
      isLoading: isLoadingCompanies,
      labelPosition: "left"
    },
    {
      name: "productId",
      label: t("deals.convertToContract.product"),
      type: "singleSelect",
      required: true,
      options: productOptions,
      defaultValue: confirmedProductId,
      placeholder: t("deals.convertToContract.selectProduct"),
      labelPosition: "left",
      onChange: handleProductChange
    },
    {
      name: "currency",
      label: t("deals.convertToContract.currency"),
      type: "singleSelect",
      required: true,
      options: currencyOptions,
      defaultValue: defaultCurrency,
      placeholder: t("deals.convertToContract.selectCurrency"),
      showSearch: true,
      labelPosition: "left"
    },
    {
      name: "contractSignDate",
      label: t("deals.convertToContract.contractSignDate"),
      type: "date",
      required: false,
      defaultValue: "",
      placeholder: t("deals.convertToContract.selectDate"),
      labelPosition: "left"
    },
    {
      name: "aeOwner",
      label: t("deals.convertToContract.aeOwner"),
      type: "singleSelect",
      required: false,
      options: aeOwnerOptions,
      defaultValue: deal?.aeOwner || deal?.managedBy || "",
      placeholder: t("deals.convertToContract.selectAeOwner"),
      showSearch: true,
      onSearch: setAeOwnerSearchTerm,
      isLoading: isLoadingAeOwners,
      renderIcon: renderAeOwnerIcon,
      labelPosition: "left",
      showSelectedItemSubtitle: false
    },
    {
      name: "csmOwner",
      label: t("deals.convertToContract.csmOwner"),
      type: "singleSelect",
      required: false,
      options: csmOwnerOptions,
      defaultValue: deal?.csmOwner || "",
      placeholder: t("deals.convertToContract.selectCsmOwner"),
      showSearch: true,
      onSearch: setCsmOwnerSearchTerm,
      isLoading: isLoadingCsmOwners,
      renderIcon: renderCsmOwnerIcon,
      labelPosition: "left",
      showSelectedItemSubtitle: false
    }
  ];

  const handleSubmit = (data: Record<string, string>) => {
    const contractData: ConvertToContractData = {
      contractName: data.contractName,
      companyId: data.companyId,
      productId: data.productId,
      currency: data.currency,
      contractSignDate: data.contractSignDate || null,
      aeOwner: data.aeOwner || null,
      csmOwner: data.csmOwner || null
    };
    onSubmit(contractData);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleConvert = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[40rem]"
        data-test={dataTest ?? "deals-convert-to-contract-modal"}
        title={
          <div className="flex flex-col gap-2">
            <Text as="div" variant="lgSubtitleH1">
              {t("deals.convertToContract.title")}
            </Text>
          </div>
        }
        content={
          <div className="flex flex-col w-[40rem]">
            <div className="flex flex-col gap-4 p-6">
              <Form
                key={`convert-${deal?.id}-${formKey}`}
                fields={formFields}
                isFormValid={(valid) => setIsFormValid(valid)}
                onSubmit={handleSubmit}
                formRef={formRef}
              />
            </div>
            <div className="flex items-center justify-center">
              <ConvertToContractBanner />
            </div>
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="deals-convert-to-contract-modal-footer"
          >
            <Button
              data-test="deals-convert-to-contract-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="deals-convert-to-contract-submit-button"
              disabled={!isFormValid}
              onClick={handleConvert}
            >
              {t("deals.convertToContract.submit")}
            </Button>
          </div>
        }
      />
      <ConfirmationModal
        isOpen={showProductWarning}
        setIsOpen={setShowProductWarning}
        title={t("deals.convertToContract.productChangeWarning.title", {
          newProductName:
            productOptions.find((p) => p.id === pendingProductId)
              ?.displayName ?? ""
        })}
        confirmationText={t(
          "deals.convertToContract.productChangeWarning.body",
          {
            currentProductName:
              productOptions.find((p) => p.id === confirmedProductId)
                ?.displayName ?? "",
            newProductName:
              productOptions.find((p) => p.id === pendingProductId)
                ?.displayName ?? ""
          }
        )}
        submitButtonText={t(
          "deals.convertToContract.productChangeWarning.confirm"
        )}
        cancelButtonText={t("cancel")}
        checkboxLabel={t(
          "deals.convertToContract.productChangeWarning.acknowledge"
        )}
        isCheckboxRequired
        isCheckboxChecked={productChangeAcknowledged}
        onCheckboxChange={setProductChangeAcknowledged}
        handleSubmit={handleProductWarningConfirm}
        handleCancel={handleProductWarningCancel}
        data-test="deals-product-change-warning-modal"
      />
    </>
  );
};

export { ConvertDealToContractModal };
